import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import useSnackbar from './useSnackbar'
import { extractErrorMessage } from '../utils/apitools'
import { isAiRefusalError } from '../utils/aiAccess'
import { GenAIInstructionsContext } from '../types/gotypes'
import type { ChatMessage } from './useDashboardInsightsChat'

/**
 * Extract the latest instructions proposal from the conversation — the text
 * inside the LAST fenced block tagged `instructions` in the LAST assistant
 * message that has one. The tag is the wire contract with the backend prompt
 * (goapi/pkg/prompts/genai_instructions.go, GenAIInstructionsBlockTag): the
 * assistant re-emits the full block on every revision, so the last one is
 * always the current proposal. Returns null when nothing has been proposed
 * yet.
 */
export function extractProposedInstructions(messages: ChatMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || !m.content) continue
    const blocks = [...m.content.matchAll(/```instructions[ \t]*\n([\s\S]*?)```/g)]
    if (blocks.length > 0) {
      const text = blocks[blocks.length - 1][1].trim()
      if (text) return text
    }
  }
  return null
}

interface UseGenAIInstructionsChatProps {
  customer: string
  /** The dialog's open state — the seeded conversation is fetched on open and
   * cleared on close (fresh each time, like the #974 insights pane; the
   * current draft rides along in the context, so continuity survives). */
  open: boolean
  /** Wizard state snapshot sent as the initial-call body. Read once per open
   * via a ref, so parent re-renders (typing in the instructions field) never
   * re-trigger the fetch. */
  context: GenAIInstructionsContext
}

interface UseGenAIInstructionsChatReturn {
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  send: (input: string) => Promise<void>
  /** The latest ```instructions proposal in the conversation, or null before
   * the assistant has proposed anything — drives "Use these instructions". */
  proposedInstructions: string | null
}

// Comment: Modeled directly on useDashboardInsightsChat.ts (#974) — same
// initial/chat two-endpoint shape, same optimistic-send-with-rollback. The
// differences: the initial body is the wizard context (no dashboard id — the
// dashboard may be unsaved), open/close is owned by the dialog's parent, and
// the hook additionally derives the current instructions proposal.
export const useGenAIInstructionsChat = ({
  customer,
  open,
  context,
}: UseGenAIInstructionsChatProps): UseGenAIInstructionsChatReturn => {
  const snackbar = useSnackbar()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The effect should re-run when `open` flips, not when the wizard state it
  // reads happens to change — ref, per the useEffect dependency rules.
  const contextRef = useRef(context)
  contextRef.current = context

  const loadInitialMessages = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const res = await axios.post<ChatMessage[]>(
        `${API_BASE_URL}/dashboards/${customer}/genai-instructions/initial`,
        contextRef.current,
      )
      setMessages(res.data)
    } catch (err) {
      const message = extractErrorMessage(err)
      console.error(message)
      setError(message)
      // A refusal shows once, in the pane; a toast would say it twice.
      if (!isAiRefusalError(err)) snackbar.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  const send = async (inputText: string) => {
    const trimmed = inputText.trim()
    if (!trimmed || isLoading) return

    const userMessage: ChatMessage = { role: 'user', content: trimmed }
    const previousConversation = messages
    const pendingConversation = [...previousConversation, userMessage]

    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)
    setError(null)

    try {
      const res = await axios.post<ChatMessage[]>(
        `${API_BASE_URL}/dashboards/${customer}/genai-instructions/chat`,
        { messages: pendingConversation },
      )
      setMessages(res.data)
    } catch (err) {
      const message = extractErrorMessage(err)
      console.error(message)
      setError(message)
      // A refusal shows once, in the pane; a toast would say it twice.
      if (!isAiRefusalError(err)) snackbar.error(message)
      setMessages(previousConversation)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!open) {
      setMessages([])
      setError(null)
      return
    }
    if (!customer) return

    loadInitialMessages()
  }, [open])

  return {
    messages,
    isLoading,
    error,
    send,
    proposedInstructions: extractProposedInstructions(messages),
  }
}

export default useGenAIInstructionsChat
